import { getCurrentLanguage } from '../locales';

// The instance's currency (Instance.currency) and money formatting for the ticketing site.
// Set from the configuration whenever lib/configuration.js loads it. Records that carry their
// own currency (ticket orders, directory events from other instances) pass it explicitly.

export const DEFAULT_CURRENCY = 'GBP';

let currentCurrency = DEFAULT_CURRENCY;

const isCurrencyCode = (code) => typeof code === 'string' && /^[A-Za-z]{3}$/.test(code);

export const setCurrency = (code) => {
  currentCurrency = isCurrencyCode(code) ? code.toUpperCase() : DEFAULT_CURRENCY;
};

export const getCurrency = () => currentCurrency;

const LOCALES = { en: 'en-GB', de: 'de-DE' };
const locale = () => {
  // Money formatting must never break a page over the language setting.
  try {
    return LOCALES[getCurrentLanguage()] || 'en-GB';
  } catch (e) {
    return 'en-GB';
  }
};

const formatter = (currency, digits) => {
  const options = {
    style: 'currency', currency, currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: digits, maximumFractionDigits: digits,
  };
  try {
    return new Intl.NumberFormat(locale(), options);
  } catch (e) {
    // narrowSymbol isn't supported everywhere
    return new Intl.NumberFormat(locale(), { ...options, currencyDisplay: 'symbol' });
  }
};

const resolve = (currency) => (isCurrencyCode(currency) ? currency.toUpperCase() : currentCurrency);

/**
 * '£12.50' (English) / '12,50 €' (German). `amount` may be a number or a decimal string from the
 * API; anything that isn't a number gives `fallback`. `options.digits` sets the decimal places
 * (default 2; 0 for whole-number summaries).
 */
export const formatMoney = (amount, currency = currentCurrency, { fallback = '', digits = 2 } = {}) => {
  if (amount === null || amount === undefined || amount === '') return fallback;
  const value = Number(amount);
  if (!Number.isFinite(value)) return fallback;
  return formatter(resolve(currency), digits).format(value);
};

/** Just the symbol, e.g. for an input addon or a 'Price (£)' label. */
export const currencySymbol = (currency = currentCurrency) => {
  const part = formatter(resolve(currency), 2).formatToParts(0).find((p) => p.type === 'currency');
  return part ? part.value : resolve(currency);
};
